/**
 * Open-from-pin path for FILE-backed workspace pins
 * (`query`/`notebook` pins whose payload is `{ source: 'file', filePath }`).
 *
 * This module is the thin KIND-ROUTER (K8): the `.sql` and `.ipynb` halves live
 * in `openSqlFilePin.ts` / `openNotebookFilePin.ts`. The shared file-open toasts
 * live here so both halves reuse them.
 *
 * Why this is a dedicated path and NOT `openScriptInTab`/`openSqlInTab`/
 * `openNotebookInTab` (spec G3):
 *  - Those helpers dedupe globally by `filePath` — so the same file pinned
 *    under two different connections would collapse into one tab. A pin's
 *    identity is its `sourcePin`, not its path: the same file under two
 *    connections must open as two independent, separately-linked tabs.
 *  - Those helpers INFER the connection binding (saved script association /
 *    current Explorer selection). A pin's binding is EXPLICIT — it comes from
 *    the pin envelope (`connectionId` + `ref.database`). Notebooks today get no
 *    binding at all; pins fix that by always passing `connectionInfo`.
 *
 * So the halves call only the raw low-level readers (`openSqlFile` /
 * `openNotebookFile`) and build the tab themselves, mirroring the helpers' tab
 * construction.
 *
 * On-focus revalidation (spec G2): a restored file tab's watcher seeds its
 * baseline hash from the current disk contents WITHOUT comparing the restored
 * (possibly stale) text — and notebooks have no watcher at all. So re-opening a
 * pin whose linked tab is already present must re-read the file and reconcile:
 *   - disk hash == tab's saved hash                 → just focus (nothing to do)
 *   - disk changed + tab clean                      → reload fresh content, focus
 *   - disk changed + tab has unsaved edits (dirty)  → focus + conflict signal,
 *                                                     NEVER silently clobber
 */
import { toast } from '../../../../components/ui/toaster';
import { getFileNameFromPath } from '../../../workspace/query-editor/utils/queryFileUtils';
import type { PinnedWorkspaceRef } from '../../../../types/electron';
import { openSqlFilePin } from './openSqlFilePin';
import { openNotebookFilePin } from './openNotebookFilePin';

/** Outcome of opening a file pin — drives the caller's analytics. */
export type FilePinOpenMode = 'focus' | 'new' | null;

/**
 * Opens (or focuses+revalidates) a file-backed workspace pin. Returns the
 * analytics mode: `'new'` when a tab was created, `'focus'` when an existing
 * linked tab was focused, `null` when nothing opened (missing/unreadable file).
 *
 * The linked-tab set is read AFTER the disk read (K3) by each half, never from
 * a pre-await snapshot — while we awaited, a rapid second open (guarded by the
 * caller's single-flight) or a tab close could have changed it.
 */
export async function openFilePin({
  connectionId,
  ref,
}: {
  connectionId: string;
  ref: PinnedWorkspaceRef;
}): Promise<FilePinOpenMode> {
  const filePath = ref.payload.source === 'file' ? ref.payload.filePath : '';

  if (ref.kind === 'notebook') {
    return openNotebookFilePin({ connectionId, ref, filePath });
  }
  return openSqlFilePin({ connectionId, ref, filePath });
}

// ---- Shared toasts (reused by both file-pin halves) -----------------------

export function showFileOpenError(filePath: string, error?: string): void {
  // Missing file mirrors the app's standard toast (see ScriptBrowserPanel);
  // other failures (corrupt/unparseable .ipynb, generic read errors) get an
  // honest generic title instead of claiming the file is missing.
  const msg = (error ?? '').toLowerCase();
  const missing = msg.includes('enoent') || msg.includes('no such file');
  toast.error({
    title: missing ? 'File not found' : 'Could not open file',
    description: filePath,
  });
}

export function warnUnsavedConflict(filePath: string): void {
  // G2 minimum-viable conflict signal — surface, do NOT silently reload.
  toast.warning({
    title: 'File changed on disk',
    description: `${getFileNameFromPath(filePath)} changed on disk — this tab has unsaved changes`,
  });
}
